'use client';

import { useQuery } from '@tanstack/react-query';
import { HotelsService, type PublicHotel, type HotelReview } from '../services/hotels.service';
import type { Hotel } from '../types/hotels.types';

type Params = Record<string, string | number | boolean | undefined>;

export const hotelsKeys = {
  all: ['hotels'] as const,
  publicList: (params?: Params) => ['hotels', 'public-list', params] as const,
  bySlug: (slug: string) => ['hotels', 'slug', slug] as const,
  rooms: (slug: string, params?: Params) => ['hotels', 'rooms', slug, params] as const,
  reviews: (slug: string) => ['hotels', 'reviews', slug] as const,
} as const;

export function mapApiToHotel(h: PublicHotel): Hotel {
  return {
    id: h.slug || h.id,
    name: h.name,
    location: [h.city, h.country].filter(Boolean).join(', '),
    latitude: h.latitude ?? 0,
    longitude: h.longitude ?? 0,
    rating: h.rating,
    reviews: h.reviewCount,
    // NULL, not 0, when the hotel has no rates.
    //
    // `?? 0` re-applied one hop downstream the exact bug removed from
    // /public/hotels: a property with no room types has no price, and 0 is
    // read as "free". FeaturedHotels compounds it by deriving
    // `oldPrice: price * 1.2`, so an unpriced hotel rendered as
    // **Rs 0, slashed from Rs 0**.
    //
    // 56 of 137 live properties are in this state (D-26/D-27).
    price: h.priceRange?.min ?? h.priceFrom ?? null,
    type: h.propertyType ?? 'Hotel',
    stars: h.starRating,
    image: h.mainImage ?? h.images?.[0] ?? `/assets/hotels/${h.slug}-main.jpg`,
    amenities: h.amenities ?? [],
    roomTypes: [],
    availability: 5,
    freeCancellation: true,
    phone: undefined,
    email: undefined,
    address: h.address,
  };
}

export function usePublicHotels(params?: Params) {
  return useQuery({
    queryKey: hotelsKeys.publicList(params),
    queryFn: () => HotelsService.listPublic(params),
    select: (res) => {
      const raw = (res as Record<string, unknown>)?.data;
      const list = Array.isArray(raw) ? (raw as PublicHotel[]) : [];
      return list.map(mapApiToHotel);
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useHotelBySlug(slug: string) {
  return useQuery({
    queryKey: hotelsKeys.bySlug(slug),
    queryFn: () => HotelsService.getBySlug(slug),
    select: (res) => {
      const raw = (res as Record<string, unknown>)?.data as PublicHotel | undefined;
      return raw ? mapApiToHotel(raw) : null;
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!slug,
  });
}

export function useHotelRooms(slug: string, params?: Params) {
  return useQuery({
    queryKey: hotelsKeys.rooms(slug, params),
    queryFn: () => HotelsService.getRoomsBySlug(slug, params),
    enabled: !!slug,
  });
}

export function useHotelRoomTypes() {
  return useQuery({
    queryKey: [...hotelsKeys.all, 'room-types'] as const,
    queryFn: () => HotelsService.getRoomTypes(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useHotelReviews(slug: string) {
  return useQuery({
    queryKey: hotelsKeys.reviews(slug),
    queryFn: () => HotelsService.getHotelReviews(slug) as Promise<{ data: HotelReview[] }>,
    select: (res) => res.data ?? [],
    staleTime: 5 * 60 * 1000,
    enabled: !!slug,
  });
}
